import React, { useState, useEffect } from 'react';
import { adminTotpService } from '../../services/adminTotpService';
import AdminTwoFactorAuthSetup from './AdminTwoFactorAuthSetup';

interface AdminTwoFactorAuthManagerProps {
  onStatusChange?: (enabled: boolean) => void;
}

const AdminTwoFactorAuthManager: React.FC<AdminTwoFactorAuthManagerProps> = ({ onStatusChange }) => {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const status = await adminTotpService.getStatus();
      setMfaEnabled(status.enabled);
      if (onStatusChange) {
        onStatusChange(status.enabled);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
    loadStatus(); // Refresh status
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!disablePassword) {
      setError('Password is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await adminTotpService.disable(disablePassword);
      setShowDisable(false);
      setDisablePassword('');
      await loadStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleReRegister = () => {
    setShowSetup(true);
  };

  if (showSetup) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setShowSetup(false)}
          className="flex items-center gap-2 text-sm font-nunito text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to 2FA Settings
        </button>
        <AdminTwoFactorAuthSetup onComplete={handleSetupComplete} onCancel={() => setShowSetup(false)} />
      </div>
    );
  }

  if (loading && !mfaEnabled) {
    return (
      <div className="flex justify-center items-center py-8">
        <svg className="animate-spin h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-start gap-3 p-4 bg-zazoo-red bg-opacity-10 border border-zazoo-red rounded-lg">
          <svg className="w-5 h-5 text-zazoo-red flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <p className="font-nunito text-zazoo-red text-sm">{error}</p>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-nunito font-semibold text-gray-900 mb-1">
              Two-Factor Authentication
            </h3>
            <p className="text-sm font-nunito text-gray-600">
              {mfaEnabled
                ? 'Your admin account is protected with Google Authenticator'
                : 'Add an extra layer of security to your admin account'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {mfaEnabled ? (
              <span className="px-3 py-1 bg-zazoo-green bg-opacity-10 text-zazoo-green text-xs font-mukta font-medium rounded-full">
                Enabled
              </span>
            ) : (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-mukta font-medium rounded-full">
                Disabled
              </span>
            )}
          </div>
        </div>

        {mfaEnabled ? (
          <div className="space-y-4">
            <div className="bg-zazoo-green bg-opacity-5 border border-zazoo-green border-opacity-20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-zazoo-green flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-nunito font-semibold text-gray-900 mb-1">
                    Two-Factor Authentication is Active
                  </p>
                  <p className="text-xs font-nunito text-gray-600">
                    You'll need to enter a code from your Google Authenticator app each time you log in.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleReRegister}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-mukta font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                Re-register (Lost Access)
              </button>
              <button
                onClick={() => setShowDisable(true)}
                className="flex-1 px-4 py-2.5 border border-zazoo-red rounded-lg text-sm font-mukta font-medium text-zazoo-red hover:bg-zazoo-red hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zazoo-red transition-colors"
              >
                Disable 2FA
              </button>
            </div>
          </div>
        ) : (
          <div>
            <button
              onClick={() => setShowSetup(true)}
              className="w-full px-6 py-2.5 bg-zazoo-accent-pink text-white rounded-lg text-sm font-mukta font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zazoo-accent-pink transition-opacity"
            >
              Enable Two-Factor Authentication
            </button>
          </div>
        )}
      </div>

      {/* Disable Modal */}
      {showDisable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-nunito font-semibold text-gray-900 mb-2">
              Disable Two-Factor Authentication
            </h3>
            <p className="text-sm font-nunito text-gray-600 mb-4">
              Enter your password to disable two-factor authentication. This will make your account less secure.
            </p>
            <form onSubmit={handleDisable} className="space-y-4">
              <div>
                <label htmlFor="disablePassword" className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="disablePassword"
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-red focus:border-transparent"
                  placeholder="Enter your password"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDisable(false);
                    setDisablePassword('');
                    setError('');
                  }}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-mukta font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !disablePassword}
                  className="flex-1 px-4 py-2.5 bg-zazoo-red text-white rounded-lg text-sm font-mukta font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zazoo-red disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Disabling...' : 'Disable 2FA'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTwoFactorAuthManager;

